import React from 'react';
import { ActiveFilters } from '@emfood/shared';

import { Chip } from '@mui/material';

import { SearchAndFilterData } from 'src/hooks/use-product-list';

import {
  AttributeType,
  getAttributesByEnum,
  getAttributeTypeLabel,
} from 'src/api/models/attributes';

interface ActiveFiltersProps {
  filterSearchModel?: SearchAndFilterData;
  onRemoveFilter: (field: keyof SearchAndFilterData, value?: string) => void;
  onClearAllFilters: () => void;
  linkedSites?: Array<{ id: string; name: string }>;
  favouritesCollections?: Array<{ id: string; name: string }>;
}

const attributeTypes = [
  AttributeType.Allergen,
  AttributeType.Category,
  AttributeType.Certification,
  AttributeType.DietaryAndLifestyle,
  AttributeType.CountryOfOrigin,
];

const getTriStateLabel = (value: boolean | undefined): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Any';
};

const getProductTypeLabel = (value: string): string => {
  switch (value) {
    case 'surplus':
      return 'Surplus';
    case 'promotion':
      return 'Promotional';
    default:
      return '';
  }
};

export default function ProductActiveFilters({
  filterSearchModel,
  onRemoveFilter,
  onClearAllFilters,
  linkedSites = [],
  favouritesCollections = [],
}: ActiveFiltersProps) {
  if (!filterSearchModel) return null;

  const {
    productName,
    availableFrom,
    // isDirectFromProducer,
    // isSmeProducer,
    producerDistance,
    distributorSiteId,
    allergens,
    categories,
    certifications,
    dietaryLifestyles,
    countryOfOrigin,
    productType,
    airfreighted,
    favouriteCollectionIds
  } = filterSearchModel;

  // Check if there are any active filters
  const hasBasicFilters =
    !!productName ||
    !!availableFrom ||
    // isDirectFromProducer === true ||
    // isDirectFromProducer === false ||
    // isSmeProducer === true ||
    // isSmeProducer === false ||
    !!producerDistance ||
    !!distributorSiteId ||
    productType ||
    airfreighted === true ||
    airfreighted === false ||
    (favouriteCollectionIds || []).length > 0;

  const hasAttributeFilters = attributeTypes.some((type) => {
    const fieldName = getAttributesByEnum(type) as keyof SearchAndFilterData;
    return ((filterSearchModel[fieldName] as string[]) || []).length > 0;
  });

  if (!hasBasicFilters && !hasAttributeFilters) return null;

  return (
    <ActiveFilters
      hasFilters={!!hasBasicFilters || hasAttributeFilters}
      onClearAllFilters={onClearAllFilters}
    >
      {productName && (
        <Chip
          key="productName"
          label={`Product Name: ${productName}`}
          onDelete={() => onRemoveFilter('productName' as keyof SearchAndFilterData)}
          onClick={() => onRemoveFilter('productName' as keyof SearchAndFilterData)}
          size="medium"
          variant="outlined"
        />
      )}

      {/* {typeof isDirectFromProducer === 'boolean' && (
        <Chip
          key="isDirectFromProducer"
          label={`Direct From Producer: ${getTriStateLabel(isDirectFromProducer)}`}
          onDelete={() => onRemoveFilter('isDirectFromProducer' as keyof SearchAndFilterData)}
          onClick={() => onRemoveFilter('isDirectFromProducer' as keyof SearchAndFilterData)}
          size="medium"
          variant="outlined"
        />
      )}

      {typeof isSmeProducer === 'boolean' && (
        <Chip
          key="isSmeProducer"
          label={`SME Producer: ${getTriStateLabel(isSmeProducer)}`}
          onDelete={() => onRemoveFilter('isSmeProducer' as keyof SearchAndFilterData)}
          onClick={() => onRemoveFilter('isSmeProducer' as keyof SearchAndFilterData)}
          size="medium"
          variant="outlined"
        />
      )} */}

      {typeof airfreighted === 'boolean' && (
        <Chip
          key="airfreighted"
          label={`Airfreighted: ${getTriStateLabel(airfreighted)}`}
          onDelete={() => onRemoveFilter('airfreighted' as keyof SearchAndFilterData)}
          onClick={() => onRemoveFilter('airfreighted' as keyof SearchAndFilterData)}
          size="medium"
          variant="outlined"
        />
      )}

      {productType && (
        <Chip
          key="productType"
          label={`Price Type: ${getProductTypeLabel(productType)}`}
          onDelete={() => onRemoveFilter('productType' as keyof SearchAndFilterData)}
          onClick={() => onRemoveFilter('productType' as keyof SearchAndFilterData)}
          size="medium"
          variant="outlined"
        />
      )}

      {producerDistance && (
        <Chip
          key="producerDistance"
          label={`Producer Locality: ${producerDistance} miles`}
          onDelete={() => onRemoveFilter('producerDistance' as keyof SearchAndFilterData)}
          onClick={() => onRemoveFilter('producerDistance' as keyof SearchAndFilterData)}
          size="medium"
          variant="outlined"
        />
      )}

      {distributorSiteId && (
        <Chip
          label={`Supplier: ${linkedSites.find((site) => site.id === distributorSiteId)?.name || distributorSiteId}`}
          onDelete={() => onRemoveFilter('distributorSiteId' as keyof SearchAndFilterData)}
          onClick={() => onRemoveFilter('distributorSiteId' as keyof SearchAndFilterData)}
          size="medium"
          variant="outlined"
        />
      )}

      {(favouriteCollectionIds || []).map((collectionId) => (
        <Chip
          key={`favouriteCollectionIds-${collectionId}`}
          label={`Favourites: ${favouritesCollections.find((collection) => collection.id === collectionId)?.name || 'Unknown list'}`}
          onDelete={() => onRemoveFilter('favouriteCollectionIds' as keyof SearchAndFilterData, collectionId)}
          onClick={() => onRemoveFilter('favouriteCollectionIds' as keyof SearchAndFilterData, collectionId)}
          size="medium"
          variant="outlined"
          sx={{ maxWidth: 240 }}
        />
      ))}

      {availableFrom && (
        <Chip
          key="availableFrom"
          label={`Available From: ${new Date(availableFrom).toLocaleDateString()}`}
          onDelete={() => onRemoveFilter('availableFrom' as keyof SearchAndFilterData)}
          onClick={() => onRemoveFilter('availableFrom' as keyof SearchAndFilterData)}
          size="medium"
          variant="outlined"
        />
      )}
      {attributeTypes.map((type) => {
        const fieldName = getAttributesByEnum(type) as keyof SearchAndFilterData;
        const values = (filterSearchModel[fieldName] as string[]) || [];

        if (values.length === 0) return null;

        return values.map((value: string) => (
          <Chip
            key={`${fieldName}-${value}`}
            label={`${getAttributeTypeLabel(type)}: ${value}`}
            onDelete={() => onRemoveFilter(fieldName as keyof SearchAndFilterData, value)}
            onClick={() => onRemoveFilter(fieldName as keyof SearchAndFilterData, value)}
            size="medium"
            variant="outlined"
          />
        ));
      })}
    </ActiveFilters>
  );
}
